import React, { useEffect } from 'react';
import { useGiftContext } from './GiftContext';
import { useNavigationGuard } from './useNavigationGuard';
import { useLocation } from 'react-router-dom';

interface GiftNavigationGuardProps {
  children: React.ReactNode;
}

export const GiftNavigationGuard: React.FC<GiftNavigationGuardProps> = ({ children }) => {
  const { handleNavigation } = useNavigationGuard();
  const giftContext = useGiftContext();
  const location = useLocation();

  useEffect(() => {
    // Check if we're currently in gift creation mode (both new and legacy URLs)
    const isInGiftCreation = location.pathname === '/start-gift' || 
                             location.pathname.includes('start-gift') ||
                             location.pathname.includes('/my-gifts/create/') ||
                             location.pathname.includes('/my-gifts/edit/');
    
    if (!isInGiftCreation) {
      return; // Only intercept when we're in gift creation
    }


    // Override header navigation
    const interceptNavigation = (event: Event) => {
      const target = event.target as HTMLElement;
      
      // Skip interception for edit buttons in Review step
      if (target.closest('.edit-option')) {
        return; // Don't intercept edit button clicks
      }
      
      // Check for main tab clicks (My Gifts & Messages, My Contacts)
      if (target.closest('.header-tab') || target.closest('.tab-mobile-version')) {
        const element = target.closest('a, button, [onClick]') as HTMLElement;
        
        if (element) {
          // Check if this is a main tab navigation (not gift creation steps)
          const isMainTabNavigation = 
            element.textContent?.includes('My Gifts') ||
            element.textContent?.includes('My Contacts');
            
          if (isMainTabNavigation) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            
            // Determine target path
            let targetPath = '/my-gifts';
            if (element.textContent?.includes('My Contacts')) {
              targetPath = '/my-contacts';
            }
            
            handleNavigation(targetPath);
            return false;
          }
        }
      }

      // Check for regular nav-link clicks (like in header tabs)
      if (target.closest('.nav-link') || target.closest('.nav-item')) {
        const element = target.closest('a, button, [onClick]') as HTMLElement;
        
        if (element) {
          // Check if this is a tab navigation outside gift creation steps
          const isTabNavigation = 
            element.textContent?.includes('My Gifts') ||
            element.textContent?.includes('My Contacts');
            
          if (isTabNavigation) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            
            // Determine target path
            let targetPath = '/my-gifts';
            if (element.textContent?.includes('My Contacts')) {
              targetPath = '/my-contacts';
            }
            
            handleNavigation(targetPath);
            return false;
          }
        }
      }
      
      // Intercept logo clicks
      if (target.closest('.header-left figure')) {
        const logoLink = target.closest('a') as HTMLAnchorElement;
        if (logoLink) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          handleNavigation('/my-gifts');
          return false;
        }
      }

      // Intercept any Link components or buttons that might navigate away
      const linkElement = target.closest('a[href]') as HTMLAnchorElement;
      if (linkElement && linkElement.href) {
        const href = linkElement.getAttribute('href') || linkElement.href;
        
        // Check if this is navigation away from gift creation
        if (href && (href.includes('/my-gifts') || href.includes('/my-contacts') || href === '/')) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          let targetPath = href;
          if (href === '/') {
            targetPath = '/my-gifts';
          }
          
          handleNavigation(targetPath);
          return false;
        }
      }
    };

    // Add multiple event listeners for better coverage
    document.addEventListener('click', interceptNavigation, true);
    document.addEventListener('mousedown', interceptNavigation, true);
    
    // Track if navigation should be allowed (e.g., after draft save)
    let allowNextNavigation = false;
    
    // Listen for navigation approval events
    const handleNavigationApproval = () => {
      allowNextNavigation = true;
    };
    
    window.addEventListener('navigationApproved', handleNavigationApproval);
    
    // Also override any programmatic navigation attempts
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(data, title, url) {
      if (url) {
        const urlString = url.toString();
        if (urlString.includes('/my-gifts') || urlString.includes('/my-contacts')) {
          
          // If navigation is approved (e.g., after draft save), allow it
          if (allowNextNavigation) {
            allowNextNavigation = false; // Reset flag
            return originalPushState.apply(this, [data, title, url]);
          }
          
          handleNavigation(urlString);
          return;
        }
      }
      return originalPushState.apply(this, [data, title, url]);
    };
    
    window.history.replaceState = function(data, title, url) {
      if (url) {
        const urlString = url.toString();
        if (urlString.includes('/my-gifts') || urlString.includes('/my-contacts')) {
          
          // If navigation is approved, allow it
          if (allowNextNavigation) {
            allowNextNavigation = false; // Reset flag
            return originalReplaceState.apply(this, [data, title, url]);
          }
          
          handleNavigation(urlString);
          return;
        }
      }
      return originalReplaceState.apply(this, [data, title, url]);
    };

    return () => {
      document.removeEventListener('click', interceptNavigation, true);
      document.removeEventListener('mousedown', interceptNavigation, true);
      window.removeEventListener('navigationApproved', handleNavigationApproval);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [handleNavigation, location.pathname]);

  return <>{children}</>;
};